import React, { useState, useEffect, useMemo } from 'react';
import { api, API_URL, apiFetch } from '../services/api';
import {
    CalendarIcon, CheckCircleIcon, LockClosedIcon, XMarkIcon, SparklesIcon,
    ChartBarIcon, ClockIcon, BookOpenIcon, ChevronLeftIcon, ArrowPathIcon
} from './icons';
import { generateDynamicCampSchedule } from '../services/smartPlannerService';

interface SharedCampPlanViewProps {
    studentId: string;
}

export const SharedCampPlanView: React.FC<SharedCampPlanViewProps> = ({ studentId }) => {
    const [student, setStudent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeDay, setActiveDay] = useState<number | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [draggedSubject, setDraggedSubject] = useState<{day: number, subject: string} | null>(null);
    const [booksQuestionCounts, setBooksQuestionCounts] = useState<any[]>([]);
    const [channels, setChannels] = useState<any[]>([]);

    const getBookName = (bookId: string, sub: string) => {
        if (!bookId) return '';
        const resBook = student?.resources?.find((r: any) => r.id === bookId);
        const name = resBook ? resBook.name : booksQuestionCounts.find((b: any) => b.bookId === bookId)?.bookName || bookId;
        return name.replace(/TYT\s+TYT/g, 'TYT').replace(/\s+/g, ' ').trim();
    };


    // Fetch student data and books question counts
    const fetchStudent = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/students/${studentId}/public`);
            if (res.ok) {
                const data = await res.json();
                setStudent(data);

                // Set default active day if not set yet (first incomplete day)
                if (data.customCampPlan && data.customCampPlan.campGenerated) {
                    const progress = data.customCampPlan.progress || {};
                    let firstIncomplete = 1;
                    for (let d = 1; d <= data.customCampPlan.campDays; d++) {
                        if (!progress[d]?.completed) {
                            firstIncomplete = d;
                            break;
                        }
                    }
                    setActiveDay(firstIncomplete);
                }
            }

            const bkRes = await apiFetch(`${API_URL}/api/book-questions`);
            if (bkRes.ok) {
                const bkData = await bkRes.json();
                setBooksQuestionCounts(bkData);
            }

            // Kütüphanenin tamamı artık giriş istiyor; paylaşılan sayfa
            // yalnızca BU planın seçtiği kanalları alıyor.
            const chRes = await apiFetch(`${API_URL}/api/camp-channels/public/${encodeURIComponent(studentId)}`);
            if (chRes.ok) {
                const chData = await chRes.json();
                setChannels(chData);
            }
        } catch (e) {
            console.error('Error fetching student:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStudent();
    }, [studentId]);

    const formatDateForDay = (startDateStr: string | null | undefined, dayOffset: number) => {
        if (!startDateStr) return '';
        const date = new Date(startDateStr);
        date.setDate(date.getDate() + (dayOffset - 1));
        return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'long' });
    };

    // Handle progress syncing to MongoDB
    const saveProgressToServer = async (updatedPlan: any, updatedProgressMap?: any) => {
        if (!student) return;

        try {
            const payload: any = {
                customCampPlan: updatedPlan
            };
            if (updatedProgressMap) {
                payload.bookProgress = updatedProgressMap;
            }

            await apiFetch(`${API_URL}/api/students/${studentId}/shared-progress`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } catch (e) {
            console.error('Error saving progress:', e);
        }
    };

    // Calculate constellation layout
    const constellationData = useMemo(() => {
        if (!student || !student.customCampPlan) return { lines: [], stars: [] };
        const N = student.customCampPlan.campDays || 30;
        const spacing = N > 1 ? 920 / (N - 1) : 0;
        const progress = student.customCampPlan.progress || {};

        const stars: any[] = [];
        const lines: any[] = [];

        // Build star coordinates
        for (let d = 1; d <= N; d++) {
            const x = 40 + (d - 1) * spacing;
            const y = 75 + Math.sin(d * 0.6) * 40;
            stars.push({ day: d, x, y });
        }

        // Build links
        for (let d = 1; d < N; d++) {
            const isCompleted = progress[d]?.completed && progress[d + 1]?.completed;
            lines.push({
                id: d,
                x1: stars[d - 1].x,
                y1: stars[d - 1].y,
                x2: stars[d].x,
                y2: stars[d].y,
                completed: isCompleted
            });
        }

        return { lines, stars };
    }, [student]);

    // Check if a day is unlocked
    const isDayUnlocked = (dayNum: number) => {
        if (!student || !student.customCampPlan) return false;
        const plan = student.customCampPlan;

        // If no start date, only require previous day completion
        if (!plan.startDate) {
            if (dayNum === 1) return true;
            return !!(plan.progress && plan.progress[dayNum - 1]?.completed);
        }

        // 1. Calendar date restriction: Cannot unlock if the day hasn't arrived yet
        const assignedDate = new Date(plan.startDate);
        assignedDate.setDate(assignedDate.getDate() + (dayNum - 1));
        assignedDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (today < assignedDate) {
            return false;
        }

        // 2. Sequential completion restriction
        if (dayNum === 1) return true;
        return !!(plan.progress && plan.progress[dayNum - 1]?.completed);
    };

    // Pre-calculate dynamic schedule
    const dynamicScheduleArray = useMemo(() => {
        if (!student || !student.customCampPlan) return [];
        return generateDynamicCampSchedule(student.customCampPlan, channels, booksQuestionCounts);
    }, [student, channels, booksQuestionCounts]);

    // Get scaled day schedule
    const getDaySchedule = (dayNum: number) => {
        if (!student || !student.customCampPlan) return null;
        const plan = student.customCampPlan;
        const customDaySchedules = plan.customDaySchedules || {};

        // If day is entirely overridden by custom schedule
        if (customDaySchedules[dayNum] && Object.keys(customDaySchedules[dayNum]).length > 0) {
            const schedule: any = {
                math: null, physics: null, chemistry: null, biology: null, turkish: null, history: null, geography: null,
                totalVideoMinutes: 0, totalQuestions: 0, activeSubjects: 0
            };
            const subjects = ['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography'];
            
            // First inherit from dynamic, then apply override
            const dynamicDay = dynamicScheduleArray[dayNum - 1] || {};
            subjects.forEach(sub => {
                const ov = customDaySchedules[dayNum][sub];
                if (ov && ov.topic) {
                    schedule[sub] = {
                        topic: ov.topic,
                        duration: ov.duration || 0,
                        videos: ov.videos || [ov.topic],
                        questions: ov.questions || 0,
                        bookId: ov.bookId || '',
                        channelName: ov.channelName || ''
                    };
                    schedule.totalVideoMinutes += ov.duration || 0;
                    schedule.totalQuestions += ov.questions || 0;
                    schedule.activeSubjects++;
                } else if (dynamicDay[sub]) {
                    schedule[sub] = dynamicDay[sub];
                    if (!schedule[sub].isExcluded && !schedule[sub].isFinished) {
                        schedule.totalVideoMinutes += schedule[sub].duration || 0;
                        schedule.totalQuestions += schedule[sub].questions || 0;
                        schedule.activeSubjects++;
                    }
                } else {
                    schedule[sub] = { isExcluded: true };
                }
            });
            return schedule;
        }

        // Return from dynamic array
        return dynamicScheduleArray[dayNum - 1] || {
            math: { isExcluded: true }, physics: { isExcluded: true }, chemistry: { isExcluded: true }, 
            biology: { isExcluded: true }, turkish: { isExcluded: true }, history: { isExcluded: true }, 
            geography: { isExcluded: true },
            totalVideoMinutes: 0, totalQuestions: 0, activeSubjects: 0
        };
    };

    // Toggle Complete on Day
    const handleToggleDayComplete = (dayNum: number) => {
        if (!student || !student.customCampPlan) return;

        const progress = { ...(student.customCampPlan.progress || {}) };
        const isCompleted = progress[dayNum]?.completed;

        progress[dayNum] = {
            ...progress[dayNum],
            completed: !isCompleted
        };

        // Initialize start date on first day check if not exists
        let startDate = student.customCampPlan.startDate;
        if (!startDate && !isCompleted) {
            startDate = new Date().toISOString();
        }

        const updatedPlan = {
            ...student.customCampPlan,
            startDate,
            progress
        };

        setStudent({
            ...student,
            customCampPlan: updatedPlan
        });

        saveProgressToServer(updatedPlan);
    };

    // Toggle subtasks (videos or books questions count)
    const handleToggleTaskComplete = (dayNum: number, taskKey: string, taskType: 'video' | 'question') => {
        if (!student || !student.customCampPlan) return;

        const progress = { ...(student.customCampPlan.progress || {}) };
        if (!progress[dayNum]) {
            progress[dayNum] = { completed: false, tasks: {} };
        }
        if (!progress[dayNum].tasks) {
            progress[dayNum].tasks = {};
        }

        const taskStatusKey = `${taskType}_${taskKey}`;
        const isTaskDone = !!progress[dayNum].tasks[taskStatusKey];
        progress[dayNum].tasks[taskStatusKey] = !isTaskDone;

        // Auto complete day if all subtasks are complete
        const sched = getDaySchedule(dayNum);
        if (sched) {
            const activeSubjects = ['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography']
                .filter(sub => sched[sub] && sched[sub].topic);

            let allDone = true;
            activeSubjects.forEach(sub => {
                const hasVideo = !!sched[sub].duration;
                const hasQ = !!sched[sub].questions;

                if (hasVideo && !progress[dayNum].tasks[`video_${sub}`]) allDone = false;
                if (hasQ && !progress[dayNum].tasks[`question_${sub}`]) allDone = false;
            });

            progress[dayNum].completed = allDone;
        }

        const updatedPlan = {
            ...student.customCampPlan,
            progress
        };

        // If it's a question, update book progress counts as well
        let updatedBookProgress = student.bookProgress || {};
        if (taskType === 'question' && !isTaskDone && sched) {
            const daySched = sched[taskKey];
            if (daySched && daySched.bookId) {
                const bookId = daySched.bookId;
                const topicsMap = updatedBookProgress[bookId] || {};
                const currentSolved = topicsMap[daySched.topic] || 0;
                topicsMap[daySched.topic] = currentSolved + daySched.questions;
                updatedBookProgress = {
                    ...updatedBookProgress,
                    [bookId]: topicsMap
                };
            }
        }

        setStudent({
            ...student,
            customCampPlan: updatedPlan,
            bookProgress: updatedBookProgress
        });

        saveProgressToServer(updatedPlan, updatedBookProgress);
    };

    // Calculate metrics & donut percentages
    const performanceMetrics = useMemo(() => {
        if (!student || !student.customCampPlan) return { dayPct: 0, videoPct: 0, questionPct: 0, overallPct: 0, completedCount: 0 };
        const plan = student.customCampPlan;
        const progress = plan.progress || {};
        const N = plan.campDays || 30;

        let completedDays = 0;
        let totalVideosCount = 0;
        let completedVideosCount = 0;
        let totalQuestionsTarget = 0;
        let completedQuestionsSolved = 0;

        for (let d = 1; d <= N; d++) {
            const sched = getDaySchedule(d);
            if (!sched) continue;

            const isDayDone = progress[d]?.completed;
            if (isDayDone) completedDays++;

            const dayTasks = progress[d]?.tasks || {};

            const subjects = ['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography'];
            subjects.forEach(sub => {
                const item = sched[sub];
                if (item && item.topic) {
                    if (item.duration) {
                        totalVideosCount++;
                        if (dayTasks[`video_${sub}`]) completedVideosCount++;
                    }
                    if (item.questions) {
                        totalQuestionsTarget += item.questions;
                        if (dayTasks[`question_${sub}`]) completedQuestionsSolved += item.questions;
                    }
                }
            });
        }

        const dayPct = Math.round((completedDays / N) * 100) || 0;
        const videoPct = Math.round((completedVideosCount / Math.max(1, totalVideosCount)) * 100) || 0;
        const questionPct = Math.round((completedQuestionsSolved / Math.max(1, totalQuestionsTarget)) * 100) || 0;
        const overallPct = Math.round(((completedVideosCount + (completedQuestionsSolved ? 1 : 0)) / Math.max(1, totalVideosCount + (totalQuestionsTarget ? 1 : 0))) * 100) || 0;

        return {
            dayPct,
            videoPct,
            questionPct,
            overallPct,
            completedCount: completedDays
        };
    }, [student, dynamicScheduleArray]);

    const subjectStats = useMemo(() => {
        const stats: {
            [sub: string]: {
                totalVideoMin: number;
                watchedVideoMin: number;
                totalQuestions: number;
                solvedQuestions: number;
                videoPct: number;
                questionPct: number;
                isEnabled: boolean;
            };
        } = {};

        const subjects = ['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography'];
        subjects.forEach(sub => {
            stats[sub] = {
                totalVideoMin: 0,
                watchedVideoMin: 0,
                totalQuestions: 0,
                solvedQuestions: 0,
                videoPct: 0,
                questionPct: 0,
                isEnabled: !!student?.customCampPlan?.enabledSubjects?.[sub]
            };
        });

        if (!student || !student.customCampPlan) return stats;
        const plan = student.customCampPlan;
        const N = plan.campDays || 30;
        const progress = plan.progress || {};

        for (let d = 1; d <= N; d++) {
            const sched = getDaySchedule(d);
            if (!sched) continue;

            const dayTasks = progress[d]?.tasks || {};

            subjects.forEach(sub => {
                const item = sched[sub];
                if (item && item.topic && !item.isFinished && !item.isExcluded) {
                    if (item.duration) {
                        stats[sub].totalVideoMin += item.duration;
                        if (dayTasks[`video_${sub}`]) {
                            stats[sub].watchedVideoMin += item.duration;
                        }
                    }
                    if (item.questions) {
                        stats[sub].totalQuestions += item.questions;
                        if (dayTasks[`question_${sub}`]) {
                            stats[sub].solvedQuestions += item.questions;
                        }
                    }
                }
            });
        }

        subjects.forEach(sub => {
            const s = stats[sub];
            s.videoPct = Math.round((s.watchedVideoMin / Math.max(1, s.totalVideoMin)) * 100) || 0;
            s.questionPct = Math.round((s.solvedQuestions / Math.max(1, s.totalQuestions)) * 100) || 0;
        });

        return stats;
    }, [student, dynamicScheduleArray]);

    // Donut SVG component helper
    const DonutChart: React.FC<{ pct: number; strokeColor: string; title: string; subtitle: string }> = ({ pct, strokeColor, title, subtitle }) => {
        const radius = 32;
        const circumference = radius * 2 * Math.PI;
        const strokeDashoffset = circumference - (pct / 100) * circumference;

        return (
            <div className="flex flex-col items-center p-4 bg-white/60 backdrop-blur-md rounded-2xl border border-gray-100 shadow-sm">
                <div className="relative w-24 h-24 flex items-center justify-center">
                    <svg className="w-24 h-24 transform -rotate-90">
                        <circle
                            cx="48"
                            cy="48"
                            r={radius}
                            stroke="rgba(0, 0, 0, 0.04)"
                            strokeWidth="6"
                            fill="transparent"
                        />
                        <circle
                            cx="48"
                            cy="48"
                            r={radius}
                            stroke={strokeColor}
                            strokeWidth="7"
                            fill="transparent"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            className="transition-all duration-500 ease-out"
                        />
                    </svg>
                    <span className="absolute text-lg font-black text-gray-800">{pct}%</span>
                </div>
                <h4 className="font-bold text-sm text-gray-800 mt-3 text-center">{title}</h4>
                <p className="text-[10px] text-gray-400 font-bold mt-0.5">{subtitle}</p>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center space-y-3">
                    <ArrowPathIcon className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
                    <h3 className="font-bold text-gray-700">Çalışma Planınız Yükleniyor...</h3>
                </div>
            </div>
        );
    }

    if (!student || !student.customCampPlan || !student.customCampPlan.campGenerated) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md text-center p-8 bg-white rounded-3xl shadow-xl border">
                    <XMarkIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Program Bulunamadı</h3>
                    <p className="text-sm text-gray-500">Danışmanınız henüz sizin için özel bir ders çalışma kampı programı oluşturmamış veya yayınlamamış.</p>
                </div>
            </div>
        );
    }

    const plan = student.customCampPlan;
    const schedToday = activeDay ? getDaySchedule(activeDay) : null;
    const dayProgressToday = activeDay ? (plan.progress?.[activeDay] || { completed: false, tasks: {} }) : null;

    return (
        <div className="min-h-screen bg-[#f8fafc] text-gray-800 font-sans pb-12">
            {/* Top Bar */}
            <header className="bg-white border-b py-5 sticky top-0 z-30 shadow-sm backdrop-blur-md bg-white/95 no-print">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-2xl shadow-lg">
                            {student.name.charAt(0)}
                        </div>
                        <div>
                            <h1 className="text-xl font-extrabold text-gray-900">{student.name} • Günlük Çalışma Planı</h1>
                            <p className="text-xs text-gray-400 font-bold mt-0.5">{plan.campName} • {plan.campDays} Günlük Eğitim Kampı</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">

                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2 no-print">
                            <span className="text-sm text-blue-800 font-extrabold">Öğrenci Görünüm Modu 🎓</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8 no-print">
                {/* Left Side: Donut charts & Progress stats */}
                <div className="lg:col-span-1 space-y-6">
                    <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
                        <ChartBarIcon className="w-5 h-5 text-blue-600" />
                        Performans Paneli
                    </h3>

                    <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
                        <DonutChart
                            pct={performanceMetrics.dayPct}
                            strokeColor="#3b82f6"
                            title="Tamamlanan Günler"
                            subtitle={`${performanceMetrics.completedCount} / ${plan.campDays} GÜN`}
                        />
                        <DonutChart
                            pct={performanceMetrics.videoPct}
                            strokeColor="#ec4899"
                            title="Video İzleme Oranı"
                            subtitle="DERS VİDEOLARI"
                        />
                        <DonutChart
                            pct={performanceMetrics.questionPct}
                            strokeColor="#10b981"
                            title="Soru Çözme Oranı"
                            subtitle="ÖDEV KİTAPLARI"
                        />
                        <DonutChart
                            pct={performanceMetrics.overallPct}
                            strokeColor="#8b5cf6"
                            title="Genel İlerleme"
                            subtitle="TÜM HEDEFLER"
                        />
                    </div>

                    <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                        <h4 className="font-extrabold text-sm text-gray-800 flex items-center gap-2 border-b pb-2.5">
                            <BookOpenIcon className="w-4.5 h-4.5 text-blue-600 animate-pulse" />
                            Ders Bazlı Gelişim
                        </h4>
                        
                        <div className="space-y-4">
                            {['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography'].map(sub => {
                                const stats = subjectStats[sub];
                                if (!stats.isEnabled || (stats.totalVideoMin === 0 && stats.totalQuestions === 0)) return null;

                                const subLabel: any = {
                                    math: 'Matematik', physics: 'Fizik', chemistry: 'Kimya', biology: 'Biyoloji', turkish: 'Türkçe', history: 'Tarih', geography: 'Coğrafya'
                                };

                                const subColorClass: any = {
                                    math: 'bg-blue-600',
                                    physics: 'bg-indigo-600',
                                    chemistry: 'bg-emerald-600',
                                    biology: 'bg-green-600',
                                    turkish: 'bg-rose-600',
                                    history: 'bg-amber-600',
                                    geography: 'bg-orange-600'
                                };

                                return (
                                    <div key={sub} className="space-y-2 border-b last:border-0 pb-3 last:pb-0">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-black text-slate-700">{subLabel[sub].toUpperCase()}</span>
                                            <span className="text-[10px] font-bold text-gray-400">
                                                Genel: {Math.round((stats.videoPct + stats.questionPct) / (stats.totalVideoMin > 0 && stats.totalQuestions > 0 ? 2 : 1))}%
                                            </span>
                                        </div>

                                        {/* 1. Watch Duration & progress bar */}
                                        {stats.totalVideoMin > 0 && (
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[10px] font-bold text-slate-500">
                                                    <span>İzlenen Süre:</span>
                                                    <span className="text-slate-700 font-extrabold">{stats.watchedVideoMin} / {stats.totalVideoMin} dk ({stats.videoPct}%)</span>
                                                </div>
                                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full ${subColorClass[sub]} transition-all duration-500`}
                                                        style={{ width: `${stats.videoPct}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}

                                        {/* 2. Question count details */}
                                        {stats.totalQuestions > 0 && (
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[10px] font-bold text-slate-500">
                                                    <span>Çözülen Soru:</span>
                                                    <span className="text-slate-700 font-extrabold">{stats.solvedQuestions} / {stats.totalQuestions} Soru ({stats.questionPct}%)</span>
                                                </div>
                                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-orange-500 transition-all duration-500"
                                                        style={{ width: `${stats.questionPct}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Right Side: Constellation, Mosaic, Today's Plan and Days Grid */}
                <div className="lg:col-span-3 space-y-8">
                    {/* Glowing constellation map */}
                    <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl"></div>
                        <div className="flex justify-between items-center mb-4 border-b border-white/10 pb-3">
                            <h4 className="font-bold text-sm text-indigo-200 tracking-wider">🌌 ALIŞKANLIK HARİTASI</h4>
                        </div>
                        <div className="w-full overflow-x-auto custom-scrollbar">
                            <svg className="min-w-[1000px] h-[150px]">
                                <g>
                                    {constellationData.lines.map(line => (
                                        <line
                                            key={line.id}
                                            x1={line.x1}
                                            y1={line.y1}
                                            x2={line.x2}
                                            y2={line.y2}
                                            stroke={line.completed ? '#8b5cf6' : 'rgba(255, 255, 255, 0.08)'}
                                            strokeWidth={line.completed ? 3.5 : 1.5}
                                            strokeDasharray={line.completed ? '' : '3,3'}
                                            className="transition-all duration-300"
                                        />
                                    ))}
                                </g>
                                <g>
                                    {constellationData.stars.map(star => {
                                        const isDone = plan.progress?.[star.day]?.completed;
                                        const isCurrent = star.day === activeDay;
                                        const unlocked = isDayUnlocked(star.day);

                                        let r = 5;
                                        let color = '#334155'; // locked/inactive
                                        if (isDone) {
                                            r = 6.5;
                                            color = '#a78bfa'; // completed
                                        } else if (isCurrent) {
                                            r = 8;
                                            color = '#3b82f6'; // active today
                                        } else if (unlocked) {
                                            color = '#64748b'; // unlocked but incomplete
                                        }

                                        return (
                                            <g
                                                key={star.day}
                                                onClick={() => { if (unlocked) setActiveDay(star.day); }}
                                                className="cursor-pointer"
                                            >
                                                <circle
                                                    cx={star.x}
                                                    cy={star.y}
                                                    r={r}
                                                    fill={color}
                                                    className="transition-all hover:scale-125"
                                                />
                                                {isCurrent && (
                                                    <circle
                                                        cx={star.x}
                                                        cy={star.y}
                                                        r={r + 3.5}
                                                        stroke="#3b82f6"
                                                        strokeWidth="1"
                                                        fill="transparent"
                                                        className="animate-ping"
                                                    />
                                                )}
                                                <title>{`Gün ${star.day}`}</title>
                                            </g>
                                        );
                                    })}
                                </g>
                            </svg>
                        </div>
                    </div>

                    {/* Today's schedule / Active day details */}
                    {activeDay && schedToday && (
                        <div className="bg-white border rounded-3xl p-6 shadow-md space-y-6">
                            <div className="flex justify-between items-center border-b pb-4">
                                <div>
                                    <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-bold">SEÇİLİ GÜN</span>
                                    <h3 className="text-2xl font-black text-gray-900 mt-1">Gün {activeDay} {plan.startDate ? `• ${formatDateForDay(plan.startDate, activeDay)}` : ''} Görev Tablosu</h3>
                                </div>
                                <button
                                    onClick={() => handleToggleDayComplete(activeDay)}
                                    className={`px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition-all active:scale-95 flex items-center gap-1.5 no-print ${dayProgressToday?.completed ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                                >
                                    <CheckCircleIcon className="w-5 h-5" />
                                    {dayProgressToday?.completed ? 'Günü Tamamlandı İşaretle' : 'Günü Tamamla'}
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(schedToday._subjectOrder || ['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography']).map((sub: string) => {
                                    const item = schedToday[sub];
                                    const subLabel: any = {
                                        math: 'Matematik', physics: 'Fizik', chemistry: 'Kimya', biology: 'Biyoloji', turkish: 'Türkçe', history: 'Tarih', geography: 'Coğrafya'
                                    };

                                    if (item && item.topic) {
                                        const videoDone = !!dayProgressToday?.tasks?.[`video_${sub}`];
                                        const questionDone = !!dayProgressToday?.tasks?.[`question_${sub}`];

                                        const subjectColor: any = {
                                            math: 'bg-blue-50/50 text-blue-800 border-blue-100',
                                            physics: 'bg-indigo-50/50 text-indigo-800 border-indigo-100',
                                            chemistry: 'bg-emerald-50/50 text-emerald-800 border-emerald-100',
                                            biology: 'bg-green-50/50 text-green-800 border-green-100',
                                            turkish: 'bg-rose-50/50 text-rose-800 border-rose-100',
                                            history: 'bg-amber-50/50 text-amber-800 border-amber-100',
                                            geography: 'bg-orange-50/50 text-orange-800 border-orange-100'
                                        };
                                        const channelSelection = plan[`selected${sub.charAt(0).toUpperCase() + sub.slice(1)}`];

                                        return (
                                            <div 
                                                key={sub} 
                                                className={`mb-3 last:mb-0 bg-white border rounded-xl shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] overflow-hidden transition-all hover:shadow-md border-slate-100 hover:border-slate-200`}
                                            >
                                                <div className={`px-3 py-2 border-b flex justify-between items-center ${subjectColor[sub]}`}>
                                                    <span className="text-[10px] font-black tracking-wider uppercase">DERS ADI: {plan.examType || 'TYT'} {subLabel[sub]}</span>
                                                </div>
                                                <div className="p-3 space-y-2.5">
                                                    <div className="flex items-start gap-2">
                                                        <span className="w-[85px] shrink-0 text-[9px] font-extrabold text-slate-400 mt-0.5 tracking-wider">KONU:</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] font-extrabold text-slate-800 leading-snug">{item.topic.toUpperCase()}</span>
                                                            {(() => {
                                                                const chanObj = channels.find(c => c.name === channelSelection);
                                                                const ytLink = chanObj?.playlistUrl || `https://www.youtube.com/results?search_query=${encodeURIComponent((channelSelection || '') + ' ' + item.topic)}`;
                                                                return (
                                                                    <a 
                                                                        href={ytLink} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="text-red-500 hover:text-red-700 transition-colors"
                                                                        title={chanObj?.playlistUrl ? "Oynatma Listesine Git" : "YouTube'da Ara"}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
                                                                    </a>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                    {channelSelection && (
                                                    <div className="flex items-start gap-2">
                                                        <span className="w-[85px] shrink-0 text-[9px] font-extrabold text-slate-400 mt-0.5 tracking-wider">KANAL ADI:</span>
                                                        <span className="text-[11px] font-bold text-slate-600 truncate leading-snug" title={channelSelection}>{channelSelection.toUpperCase()}</span>
                                                    </div>
                                                    )}

                                                    {item.duration > 0 && (
                                                        <div
                                                            onClick={() => handleToggleTaskComplete(activeDay, sub, 'video')}
                                                            className={`flex items-center justify-between p-2.5 mt-3 bg-white border rounded-xl cursor-pointer hover:shadow-sm transition-all ${videoDone ? 'border-green-200 bg-green-50/20 text-green-700' : 'border-slate-100 bg-slate-50'}`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={videoDone}
                                                                    onChange={() => {}} // Handle parent click
                                                                    className="w-4 h-4 rounded text-green-600"
                                                                />
                                                                <span className="text-[11px] font-bold">Video Dersi İzle</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[9px] font-extrabold text-slate-400 tracking-wider">SÜRE:</span>
                                                                <span className="text-[11px] font-extrabold text-indigo-600">{item.duration} DK</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {item.questions > 0 && (
                                                        <div
                                                            onClick={() => handleToggleTaskComplete(activeDay, sub, 'question')}
                                                            className={`flex items-center justify-between p-2.5 mt-2 bg-white border rounded-xl cursor-pointer hover:shadow-sm transition-all ${questionDone ? 'border-green-200 bg-green-50/20 text-green-700' : 'border-slate-100 bg-slate-50'}`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={questionDone}
                                                                    onChange={() => {}}
                                                                    className="w-4 h-4 rounded text-green-600"
                                                                />
                                                                <span className="text-[11px] font-bold">
                                                                    {item.bookId ? getBookName(item.bookId, sub) : 'Soru'}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[9px] font-extrabold text-slate-400 tracking-wider">HEDEF:</span>
                                                                <span className="text-[11px] font-extrabold text-orange-600">{item.questions} SORU</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                                {schedToday.activeSubjects === 0 && (
                                    <div className="col-span-2 text-center py-8 text-gray-400 italic">Bugün için programlanmış görev bulunmamaktadır.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* All Days Calendar Cards Grid */}
                    <div className="space-y-4">
                        <h4 className="font-extrabold text-gray-700 text-lg">Tüm Kamp Takvimi</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Array.from({ length: plan.campDays }).map((_, idx) => {
                                const dayNum = idx + 1;
                                const sched = getDaySchedule(dayNum);
                                const isCompleted = plan.progress?.[dayNum]?.completed;
                                const unlocked = isDayUnlocked(dayNum);

                                return (
                                    <div
                                        key={`shared-grid-day-${dayNum}`}
                                        onClick={() => { if (unlocked) setActiveDay(dayNum); }}
                                        className={`p-4 border rounded-2xl text-left cursor-pointer transition-all ${isCompleted ? 'border-green-300 bg-green-50/10' : unlocked ? 'border-slate-200 bg-white hover:border-blue-400' : 'border-slate-100 bg-slate-100/50 cursor-not-allowed opacity-50'}`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-black text-gray-600">Gün {dayNum} {plan.startDate ? `• ${formatDateForDay(plan.startDate, dayNum)}` : ''}</span>
                                            {!unlocked && <LockClosedIcon className="w-3.5 h-3.5 text-gray-400" />}
                                            {isCompleted && <span className="text-green-500">✓</span>}
                                        </div>
                                        {sched && (
                                            <div className="mt-2 text-[10px] text-gray-400 font-bold flex gap-2">
                                                {sched.totalVideoMinutes > 0 && <span>🎥 {sched.totalVideoMinutes}dk</span>}
                                                {sched.totalQuestions > 0 && <span>✍️ {sched.totalQuestions}Q</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
};
